/**
 * Shapes and string unions for the Docebo embedded course player (DSDK events).
 * @see https://developer.docebo.com/docs/embedded-learning-building-blocks-course-player-widget
 */

export type DoceboCoursePlayerType = 'static' | 'dynamic';
export type DoceboTmDownloadMode = 'blob' | 'link';
export type DoceboGlobalSearchFilter = 'courses_and_learning_plans' | 'course_content' | 'course_catalogs';

/** `view` on `view_changed` and widget lifecycle payloads (`FlowView` in Docebo docs). */
export type DoceboFlowView = 'preview' | 'toc' | 'viewer' | 'viewer_toc' | 'waiting_list' | 'warning';

/** `type` on `course_loaded` / `course_status_changed` payloads. */
export type DoceboCourseKind = 'elearning' | 'classroom' | 'webinar' | 'learning_plan';

/** `status` when access is restricted; `null` when not applicable (Docebo examples). */
export type DoceboCourseAccessStatus = 'max_quota' | 'expired' | 'validity_in_future' | 'user_unauthorized';

export type DoceboCourseEnrollmentStatus =
    'in_progress' | 'waiting_list' | 'completed' | 'suspended' | 'overbooked' | 'enrolled' | 'not_started';

export type DoceboCourseEnterStatus =
    'in_waiting_list' | 'in_waiting_list_play' | 'in_waiting_list_enroll' | 'locked' | 'buy' | 'enroll' | 'play';

/**
 * `type` on `warning_shown` when the emitter is the single course player.
 * Other embed widgets may emit other `type` strings.
 */
export type DoceboWarningSlateType =
    | 'widget_invalid_configuration'
    | 'widget_no_content'
    | 'widget_no_course'
    | 'user_authentication_failed'
    | 'user_unauthorized'
    | 'user_not_enrolled'
    | 'user_in_waiting_list'
    | 'training_material_list_empty'
    | 'training_material_error'
    | 'training_material_with_prerequisites'
    | 'purchase_info'
    | 'no_result_search'
    | 'course_max_quota'
    | 'course_validity_in_future'
    | 'course_expired';

/** `type` on each entry in `training_materials_list` (content tab). */
export type DoceboTrainingMaterialListItemKind =
    | 'scormorg'
    | 'aicc'
    | 'tincan'
    | 'video'
    | 'youtube'
    | 'wistia'
    | 'vimeo'
    | 'file'
    | 'deliverable'
    | 'htmlpage'
    | 'poll'
    | 'test'
    | 'sign'
    | 'googledrive'
    | 'authoring';

/** When `locked` is a string on a training material list row. */
export type DoceboTrainingMaterialListLockedReason = 'max_number_views' | 'locked_maxattempts' | 'locked';

/** `type` on `training_material_status_changed`. */
export type DoceboTrainingMaterialStatusKind =
    'aicc' | 'scormorg' | 'video' | 'tincan' | 'wistia' | 'vimeo' | 'file' | 'htmlpage' | 'youtube';

export type DoceboTrainingMaterialProgressStatus =
    'in_progress' | 'incomplete' | 'completed' | 'passed' | 'attempted' | 'failed';

export interface DoceboWidgetCreatedPayload {
    id: string;
    /** Docebo: `WidgetType` — values are not listed in the course player doc. */
    type: string;
    view: DoceboFlowView;
}

export type DoceboWidgetDestroyedPayload = DoceboWidgetCreatedPayload;

export interface DoceboCoursePayload {
    id: string;
    name: string;
    type: DoceboCourseKind;
    status: DoceboCourseAccessStatus | null;
    enrollment_status: DoceboCourseEnrollmentStatus;
    enter_status: DoceboCourseEnterStatus;
    emitter: string;
}

export interface DoceboPlaybackPayload {
    trainingMaterialId: string;
    name: string;
    emitter: string;
}

export interface DoceboVolumeLevelChangedPayload {
    level: number;
    emitter: string;
}

/** Docebo documents only `fullscreen`; no `emitter` on this event. */
export interface DoceboWidgetFullscreenPayload {
    fullscreen: boolean;
}

export interface DoceboViewChangedPayload {
    view: DoceboFlowView;
    emitter: string;
}

export interface DoceboTrainingMaterialsListLoadedPayload {
    training_materials_list: Array<{
        id: string;
        name: string;
        type: DoceboTrainingMaterialListItemKind;
        locked: boolean | DoceboTrainingMaterialListLockedReason;
        status: string;
    }>;
    emitter: string;
}

export interface DoceboWarningShownPayload {
    type: DoceboWarningSlateType;
    emitter: string;
}

export interface DoceboTrainingMaterialStatusChangedPayload {
    emitter: string;
    id: string;
    name: string;
    type: DoceboTrainingMaterialStatusKind;
    status: DoceboTrainingMaterialProgressStatus;
}

export type DsdkEventName =
    | 'widget_created'
    | 'widget_destroyed'
    | 'course_loaded'
    | 'course_status_changed'
    | 'playback_started'
    | 'playback_paused'
    | 'playback_reset'
    | 'volume_level_changed'
    | 'widget_fullscreen'
    | 'view_changed'
    | 'training_materials_list_loaded'
    | 'warning_shown'
    | 'training_material_status_changed';

/** Event names registered on `DSDK.getEventManager()` — single source of truth for the bridge + player. */
export const DOCEBO_COURSE_PLAYER_DSDK_EVENT_NAMES = [
    'widget_created',
    'widget_destroyed',
    'course_loaded',
    'course_status_changed',
    'playback_started',
    'playback_paused',
    'playback_reset',
    'volume_level_changed',
    'widget_fullscreen',
    'view_changed',
    'training_materials_list_loaded',
    'warning_shown',
    'training_material_status_changed'
] as const satisfies readonly DsdkEventName[];
